import {
  createTheme,
  rem,
  type CSSVariablesResolver,
  type MantineColor,
  type MantineThemeColors,
} from '@mantine/core';
import * as components from './components';

export const addMantineColors = (
  colors: [
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
  ]
): MantineThemeColors[MantineColor] => {
  return [
    colors[0],
    colors[1],
    colors[2],
    colors[3],
    colors[4],
    colors[5],
    colors[6],
    colors[7],
    colors[8],
    colors[9],
  ];
};

export const addMantineSingleColor = (
  color: string
): MantineThemeColors[MantineColor] => {
  return [color, color, color, color, color, color, color, color, color, color];
};

export const theme = createTheme({
  components,
  fontFamily: 'Manrope',
  primaryColor: 'primary',
  radius: {
    // Todo-Omarov копипаст с Wetail Avia
    default: rem(8),
    xs: rem(6),
    lg: rem(12),
  },
  breakpoints: {
    // Todo-Omarov копипаст с Wetail Avia
    xs: '23.4375em',
    sm: '48em',
    md: '80em', // 1280px
    lg: '90em',
    xl: '120em',
  },
  colors: {
    primary: addMantineColors([
      '#EEF1F6',
      '#B3C1D7',
      '#778FB7',
      '#5777B9',
      '#344C7D',
      '#283A60',
      '#26375C',
      '#18233A',
      '#0C121D',
      '#090D16',
    ]),
    secondary: addMantineColors([
      '#EEF6FF', // 0
      '#D5E8FF', // 1
      '#C7E1FF', // 2
      '#A2D3FD', // 3
      '#84BCFF', // 4
      '#73B3FF', // 5
      '#3B95FF', // 6
      '#1F86FF', // 7
      '#0065DC', // 8
      '#0052B3', // 9
    ]),
    tertiary: addMantineColors([
      '#F1F1F3',
      '#D6D5DB',
      '#C8C7CF',
      '#8E8C9C',
      '#7E7C8E',
      '#6D6B7C',
      '#5F5E6D',
      '#5A5967',
      '#454550',
      '#2D2D34',
    ]),
    blue: addMantineColors([
      '#E8F2FF',
      '#D6E8FF',
      '#C3DEFF',
      '#ABD0FF',
      '#92C2FF',
      '#74B4FF',
      '#5397F1',
      '#247BE2',
      '#1669D0',
      '#0057BF',
    ]),
    green: addMantineColors([
      '#EBF7E7',
      '#DCF3CC',
      '#CCEFB2',
      '#B5E094',
      '#9DD277',
      '#85C359',
      '#71B242',
      '#5CA22A',
      '#4A9419',
      '#388700',
    ]),
    yellow: addMantineColors([
      '#FFF4D8',
      '#FFEAB6',
      '#FFE193',
      '#FFD673',
      '#FFCC50',
      '#FFC122',
      '#FAAD29',
      '#EB8E21',
      '#D67011',
      '#C05100',
    ]),
    red: addMantineColors([
      '#FFF1ED',
      '#FFD9D0',
      '#FFC1B4',
      '#FFAF9A',
      '#FF9D80',
      '#FF8A66',
      '#FF6E46',
      '#FF4C25',
      '#E83A14',
      '#D22600',
    ]),
    formBackground: addMantineSingleColor('rgba(255, 255, 255, 0.70)'),
    otherWhite: addMantineSingleColor('#FFFFFF'),
    otherBlack: addMantineSingleColor('#1A1A18'),
    tableHeaderBackground: addMantineSingleColor('#EEF1F6'),
    menuItemSection: addMantineSingleColor('#EEF1F6'),
    badgeWhite: addMantineSingleColor('rgba(255, 255, 255, 0.20)'),
    badgeDark: addMantineSingleColor('rgba(33, 37, 41, 0.20)'),
    badgeTertiary: addMantineSingleColor('rgba(78, 78, 78, 0.16)'),
    badgeLightSkill: addMantineSingleColor('rgba(255, 255, 255, 0.2)'),
    badgeGray: addMantineSingleColor('rgba(97, 110, 122, 0.2)'),
    badgeDarkSkill: addMantineSingleColor('rgba(18, 18, 18, 0.2)'),
    badgeSecondary: addMantineSingleColor('rgba(132, 189, 236, 0.16)'),
    badgeGreen: addMantineSingleColor('rgba(62, 203, 76, 0.16)'),
    badgeRed: addMantineSingleColor('rgba(255, 138, 102, 0.16)'),
    badgeBlue: addMantineSingleColor('rgba(36, 123, 226, 0.2)'),
    badgeTertiaryLightSkill: addMantineSingleColor('rgba(255, 193, 145, 0.16)'),
  },
  fontSizes: {
    '3xs': rem(8),
    '2xs': rem(10),
    xs: rem(12),
    sm: rem(14),
    md: rem(16),
    lg: rem(18),
    xl: rem(20),
  },
  lineHeights: {
    '3xs': rem(10),
    '2xs': rem(12),
    xs: rem(14),
    sm: rem(16),
    md: rem(18),
    lg: rem(24),
    xl: rem(26),
  },
  headings: {
    fontFamily: 'Manrope',
    fontWeight: '600',
    sizes: {
      h1: {
        fontSize: rem(48),
        lineHeight: rem(64),
      },
      h2: {
        fontSize: rem(40),
        lineHeight: rem(50),
      },
      h3: {
        fontSize: rem(32),
        lineHeight: rem(40),
      },
      h4: {
        fontSize: rem(24),
        lineHeight: rem(32),
      },
      h5: {
        fontSize: rem(18),
        lineHeight: rem(24),
      },
      h6: {
        fontSize: rem(14),
        lineHeight: rem(16),
      },
    },
  },
  spacing: {
    // Todo-Omarov копипаст с Wetail Avia
    '5xs': rem(2),
    '4xs': rem(4),
    '3xs': rem(6),
    '2xs': rem(8),
    xs: rem(10),
    sm: rem(12),
    md: rem(14),
    lg: rem(16),
    xl: rem(18),
    '2xl': rem(20),
    '3xl': rem(24),
    '4xl': rem(32),
    '5xl': rem(48),
  },
});

export const resolver: CSSVariablesResolver = () => ({
  // Todo-Omarov копипаст с Wetail Avia
  variables: {
    '--mantine-icon-xs': rem(8),
    '--mantine-icon-4sm': rem(10),
    '--mantine-icon-3sm': rem(12),
    '--mantine-icon-2sm': rem(14),
    '--mantine-icon-sm': rem(16),
    '--mantine-icon-md': rem(24),
    '--mantine-icon-lg': rem(28),
    '--mantine-icon-xl': rem(32),
  },
  dark: {},
  light: {},
});
